import { useMemo, useState } from 'react'
import { X, Minus, Plus, Check, ScanLine, AlertTriangle } from 'lucide-react'
import { ProductIcon, getCategoryIcon } from '@/lib/icons'
import { formatCurrency } from '@/lib/utils'
import { withCdnImageWidth } from '@/lib/cdnImage'
import { potencySuffix } from '@/lib/potency'
import { useCartStore } from '@/stores/cartStore'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useProductBatches } from '@/api/products'
import { usePOSConfig } from '@/api/config'
import { computeSaleCap, isAtSaleCap } from '@/lib/cartCap'
import { useBlockedCartLines } from '@/hooks/useBlockedCartLines'
import type { CartItem, Product } from '@/types'

// Delivery-fee sentinel (DeliverySpecialProduct, id -123 on the BE). The
// delivery charge is part of a delivery order's contract, not a normal cart
// line — its row is read-only (no remove, no qty change). The BE rejects a
// void on it too (code: delivery_line_not_removable).
const DELIVERY_PRODUCT_ID = -123

export interface CartItemRowProps {
  item: CartItem
  onQuantityChange: (itemId: string, delta: number) => void
  onRemove: (itemId: string) => void
  onProductClick?: (product: Product, cartItemId?: string) => void
  canIncrease?: boolean
  isRemoving?: boolean
  isFocused?: boolean
}

const strainStyles: Record<string, string> = {
  Indica: 'bg-purple-100 text-purple-700',
  Sativa: 'bg-orange-100 text-orange-700',
  Hybrid: 'bg-green-100 text-green-700',
  'N/A': 'bg-gray-100 text-gray-600',
}

// Mirrors ProductCard.isMeaningfulStrain — backend uses several "no strain"
// sentinels (empty, "N/A", "-", "none") that we don't want to render as
// blank/dash badges.
function isMeaningfulStrain(strain: unknown): strain is string {
  // The Product type declares strain as string but the BE sometimes
  // returns an array (e.g. multi-strain products) or null. Coerce-and-
  // check rather than trusting the type.
  if (typeof strain !== 'string') return false
  const normalized = strain.trim().toLowerCase()
  return normalized !== '' && normalized !== '-' && normalized !== 'n/a' && normalized !== 'none'
}

function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '0'
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (!Number.isFinite(num)) return '0'
  if (num % 1 === 0) return `${num}`
  return num.toFixed(2).replace(/\.?0+$/, '')
}

function shouldShowCbd(cbd: string | number | null | undefined): boolean {
  if (cbd === null || cbd === undefined) return false
  const num = typeof cbd === 'number' ? cbd : parseFloat(cbd)
  if (!Number.isFinite(num)) return false
  return num > 0.5
}

// Show the weight whenever the product has a positive `mj_grams`, regardless of
// `is_cannabis`. Prefer the human-readable `product_unit` (e.g. "3x0.5g") when
// available; otherwise fall back to `mj_grams` rounded to a whole number + "g".
function getDisplayWeight(product: Product): string {
  const raw = product.mj_grams
  const grams = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''))
  if (!Number.isFinite(grams) || grams <= 0) return ''
  if (product.product_unit) return product.product_unit
  return `${Math.round(grams)}g`
}

function getProductFields(product: Product) {
  const isCannabis = product.is_cannabis === true
  // Legacy /ht/api/objects/products/:id can return `strain` and `category`
  // as numeric FKs to lookup tables instead of names. Prefer typed-string
  // values and fall through when the field is anything else, so the
  // strain badge and category-icon lookup don't get a number when the
  // refund-flow product enrichment hits that endpoint.
  const strain =
    (typeof product.strain === 'string' && product.strain) ||
    (typeof product.strain_type === 'string' && product.strain_type) ||
    ''
  const category =
    (typeof product.category === 'string' && product.category) ||
    (typeof product.category_name === 'string' && product.category_name) ||
    ''
  return {
    name: product.display_name || product.description || product.name || 'Unknown Product',
    brand: product.brand_name || product.vendor_name || product.brand || '',
    category,
    strain,
    thc: isCannabis ? (product.test_thc ?? product.thc_percent ?? null) : null,
    unit: isCannabis ? (product.test_units ?? null) : null,
    cbd: isCannabis ? (product.test_cbd ?? product.cbd_percent ?? null) : null,
    size: getDisplayWeight(product),
    icon: getCategoryIcon(category),
    image: product.image_url || product.thumbnail_url || product.image || null,
  }
}

export function CartItemRow({
  item,
  onQuantityChange,
  onRemove,
  onProductClick,
  canIncrease = true,
  isRemoving = false,
  isFocused = false
}: CartItemRowProps) {
  const { product, quantity } = item
  const p = getProductFields(product)
  const [imgError, setImgError] = useState(false)

  // Batch-level THC / CBD override. The product card shows the SKU-level
  // average, but each batch has its own COA-tested potency. When the
  // cashier locked the line to a specific lot we surface that batch's
  // numbers instead so the cart row matches the receipt the customer
  // gets. Falls through to the product-level values when no lot is set
  // or the batch has no recorded potency. The hook is only enabled for
  // cannabis lines with a lot; React Query dedupes per product across
  // rows so this is a single network call regardless of cart size.
  const isCannabis = product.is_cannabis === true
  const hasLot = !!item.lot_number
  const { data: batches } = useProductBatches(item.product_id, isCannabis && hasLot)
  const batch = useMemo(() => {
    if (!batches || !item.lot_number) return null
    return batches.find((b) => b.lot_number === item.lot_number) ?? null
  }, [batches, item.lot_number])
  const thc = batch?.thc != null ? batch.thc : p.thc
  const cbd = batch?.cbd != null ? batch.cbd : p.cbd
  // A lot's numbers carry the lot's unit, as in the lot picker; the product's
  // unit is for the product's numbers.
  const unit = batch && (batch.thc != null || batch.cbd != null) ? (batch.test_units ?? null) : p.unit

  // Did /cart/preview refuse to price THIS line? The BE answers 200 with the
  // line absent from `items` and the reason in `warnings` (e.g. the clerk-POS
  // oversell gate), which is why the row below would otherwise keep showing its
  // catalog price while contributing $0.00 to a cart whose Checkout button also
  // reads $0.00. /cart/commit hard-rejects the same line, so this is the only
  // warning the cashier gets before the tender fails.
  const blockedReason = useBlockedCartLines().byItemId.get(item.id) ?? null

  // Server-priced unit price from /cart/preview. The legacy posStore
  // `unit_price` / `line_total` fields are only updated by the v1
  // useSyncReceiptTotals hook, which is no longer mounted in the v2
  // /cart/preview architecture. Without this lookup the cart row
  // displays the catalog/regular price (e.g. $86.92) even after the
  // server confirms a customer-tier price (e.g. $70.47), so the line
  // total disagrees with the Subtotal/Total readings below.
  //
  // Match the local cart item to the priced row by
  // `${product_id}|${lot_number ?? ''}` — same key applyPreviewToLines
  // uses. Fall back by product_id alone when the BE priced row omits
  // lot_number (products without batch tracking) and there's exactly
  // one priced row for the product, so the cart line still picks up
  // the customer-tier price instead of falling through to the catalog
  // shelf price.
  const previewItems = useCartStore((s) => s.preview?.items ?? null)
  const priced = useMemo(() => {
    if (!previewItems) return null
    const key = `${item.product_id}|${item.lot_number ?? ''}`
    const exact = previewItems.find(
      (it) =>
        `${it.product_id}|${(it as { lot_number?: string | null }).lot_number ?? ''}` === key,
    )
    if (exact) return exact
    if (item.lot_number) {
      const candidates = previewItems.filter((it) => it.product_id === item.product_id)
      if (candidates.length === 1) return candidates[0]
    }
    return null
  }, [previewItems, item.product_id, item.lot_number])

  const legacyUnitPrice =
    typeof item.unit_price === 'number' ? item.unit_price : parseFloat(String(item.unit_price)) || 0
  const previewSubtotal = priced ? parseFloat(String(priced.subtotal)) : NaN
  const previewQty = priced ? parseFloat(String((priced as { qty?: string | number }).qty ?? 1)) : 1
  // Per-unit POST-discount price. The BE may consolidate two FE lot
  // lines into one preview item (qty=2, subtotal=$53.28) — using
  // `priced.subtotal` directly as each line's total then double-counts
  // the consolidated row, so both rows display $53.28 and the cart sums
  // to twice the actual receipt. Derive a per-unit price and scale by
  // THIS line's qty so each row shows its own share. Falls back to the
  // pre-discount `priced.price` only when subtotal is missing.
  const previewUnitSubtotal =
    Number.isFinite(previewSubtotal) && previewQty > 0 ? previewSubtotal / previewQty : NaN
  const previewUnitPrice = Number.isFinite(previewUnitSubtotal)
    ? previewUnitSubtotal
    : (priced ? parseFloat(String(priced.price)) : NaN)

  const unitPrice = Number.isFinite(previewUnitPrice) ? previewUnitPrice : legacyUnitPrice
  // Sale carts: server `subtotal` already accounts for tier + scheduled
  // discount AND per-line `discount_amount` (legacy `discount_item`
  // scope) when the preview body included items[].discount_amount. The
  // bridge propagates the cashier's input from posStore.cart →
  // cartStore.lines so it reaches the preview body — see
  // useV2PreviewBridge. Refund carts have negative qty in the local cart
  // but the server returns a positive subtotal, so preserve the cart's
  // sign here.
  const lineTotal = Number.isFinite(previewUnitSubtotal)
    ? previewUnitSubtotal * quantity
    : (item.line_total || legacyUnitPrice * quantity)

  // Discount displayed on the line — the cashier's input from posStore.
  // We deliberately do NOT use `priced.discount_amount` from the server
  // here: the BE field's semantics are ambiguous (it can include
  // schedule/tier amounts, next-tier targets, or compound values
  // depending on the discount path) and using it produced visibly wrong
  // line-discount labels (e.g. "$80 off" when the cashier set $40).
  // The local `item.discount_amount` is the line-total dollar discount
  // the cashier explicitly entered — that's what the discount label
  // should read.
  const rawLineDiscount = Math.abs(item.discount_amount ?? 0)
  const absQty = Math.max(1, Math.abs(quantity))
  const perUnitDiscount = rawLineDiscount / absQty

  // Implicit savings — anything baked into the priced unit_price (member
  // price2, scheduled discount, or any other tier reduction). Compares
  // the catalog `regular_price` against the live line total so the row
  // mirrors the OrderDetail row's display: strike-through original +
  // (-$X) savings. Falls back to product.price when regular_price is
  // missing, and to the line's own unit_price as a last resort so we
  // never compute a negative "savings" against ourselves.
  const regularUnitPriceRaw = typeof product.regular_price === 'number'
    ? product.regular_price
    : parseFloat(String(product.regular_price ?? ''))
  const regularUnitPriceFallback = typeof product.price === 'number'
    ? product.price
    : parseFloat(String(product.price ?? ''))
  const regularUnitPrice = Number.isFinite(regularUnitPriceRaw) && regularUnitPriceRaw > 0
    ? regularUnitPriceRaw
    : (Number.isFinite(regularUnitPriceFallback) && regularUnitPriceFallback > 0
        ? regularUnitPriceFallback
        : unitPrice)
  const regularLineTotal = regularUnitPrice * absQty
  // Compare per-unit so sub-cent rounding doesn't compound into a phantom
  // "savings" at higher quantities (e.g. catalog $7.1833 vs live $7.18 ×3
  // would otherwise display as -$0.01 off).
  const perUnitSavings = regularUnitPrice - Math.abs(unitPrice)
  const hasSavings = perUnitSavings > 0.005
  const totalSavings = hasSavings ? Math.max(0, regularLineTotal - Math.abs(lineTotal)) : 0
  const onHandRaw = product.on_hand
  const onHandNum = typeof onHandRaw === 'number' ? onHandRaw : parseFloat(String(onHandRaw ?? ''))
  const onHand = Number.isFinite(onHandNum) ? Math.max(0, Math.floor(onHandNum)) : null
  // Basket cart lines are immutable on the backend (PUT qty/price returns
  // 409 modify_basket_as_unit). Disable qty controls; deletion still works
  // because DELETE cascades to all children. See V2-API.md § Baskets.
  const isBasket = (item.basket_id ?? null) !== null
  // Gift cards are 1:1 with a card number — each row is bound to a
  // specific card + balance, so qty stays at 1. Selling more is meaningless
  // (you'd need a second card with its own number). Removal happens via
  // the X button.
  const isGiftCard = !!item.gift_card_number

  // The delivery fee line is read-only in the POS — it can't be removed or
  // re-quantified. (BE also enforces this on void.)
  const isDelivery = item.product_id === DELIVERY_PRODUCT_ID

  const isRefundItem = quantity < 0
  const displayQuantity = Math.abs(quantity)

  // Sale-side on-hand cap. See computeSaleCap for the precedence rules.
  // Refund items skip this entirely; their cap is `max_refund_qty`,
  // propagated via the `canIncrease` prop.
  const { data: posConfig } = usePOSConfig()
  const verifyOverOnHand = posConfig?.posVerifyIfIsOverOnHand !== false
  const saleCap = computeSaleCap(item, batches, verifyOverOnHand)
  const saleAtCap = !isRefundItem && isAtSaleCap(quantity, saleCap)
  const finalCanIncrease = canIncrease && !saleAtCap

  // Refund-cart mutations round-trip to the BE (PUT qty / DELETE line).
  // Allowing the cashier to click +/-/X while a prior PUT or DELETE is
  // in flight starts a parallel mutation: the BE serializes via
  // with_for_update, but the network can deliver responses out of order
  // and the older reseed then overwrites the newer totals (cart shows
  // one qty, totals reflect another). Same race applies to the rollback
  // path on a failed PUT. Disable the controls for the in-flight window
  // so each mutation completes (or fails) before the next one starts.
  // Only refund lines are locked; sale-mode rows are unaffected because
  // isRefundMutating is always false in sale mode.
  const isRefundMutating = usePOSStore((s) => s.isRefundMutating)
  const refundLocked = isRefundItem && isRefundMutating

  // Lock item edits on a partially-paid receipt. Editing items after a
  // payment has landed creates a FE/BE divergence -- the BE has charged
  // the customer against the N-item ledger but the cart shows N-1, and
  // the cashier ends up reading the wrong "what's owed" off a desynced
  // screen. Real production case: Ingersoll 2026-05-26 receipt
  // S099R296-260526-00017 -- $28.75 paid against a 2-item $54.16 cart,
  // one item removed from the local cart during the 51-min handoff,
  // cashier saw "1 item / Checkout $54.16" and had to fall back to a
  // standalone Moneris transaction to close the balance.
  //
  // The recovery path for a legitimate edit is "void the payment in
  // Checkout, then edit" -- raw cart edits while money sits on the
  // ledger are blocked here. Refund mode has its own per-line guards
  // (max_refund_qty, isRefundMutating) and is excluded. The same
  // predicate is the data-safety backstop on posStore's mutating
  // actions (addToCart / updateCartItem / updateQuantity /
  // removeFromCart / changeBatch).
  const paymentLocked = usePOSStore((s) =>
    isReceiptPartiallyPaid(s.serverTotals, s.isRefundMode),
  )
  // Online-PAID pickup view: the whole cart is a read-only mirror of the
  // charged order -- no removes, no qty changes (no partial changes by
  // design; cancel the entire order instead).
  const paidOnlineLocked = usePOSStore((s) =>
    Boolean(s.loadedEcomMeta?.paidOnline),
  )
  const editLocked = refundLocked || paymentLocked || isDelivery || paidOnlineLocked
  const lockTooltip = isDelivery
    ? 'The delivery fee is part of this delivery order and can’t be removed'
    : paidOnlineLocked
      ? 'Order was paid online — items can’t be changed. Cancel the order for a full refund instead.'
    : paymentLocked
      ? 'Receipt has payment(s) applied — void the payment in Checkout to edit items'
      : refundLocked
        ? 'Updating refund — wait for the server to respond'
        : undefined
  // Whether the cap was per-lot (vs SKU on-hand) — drives the tooltip
  // wording so the cashier knows it's the lot, not the SKU, that's empty.
  // Use `batch != null` so a lot with quantity=0 still gets the lot-
  // specific message (saleCap=0, but the cap source was still the lot).
  const lotCapHit =
    saleAtCap
    && !!item.lot_number
    && batch != null
    && batch.quantity != null
    && Number.isFinite(Number(batch.quantity))

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        isDelivery ? '' : 'hover:border-green-400 cursor-pointer'
      } ${
        isRemoving ? 'animate-fade-out' : 'animate-slide-in'
      } ${
        blockedReason
          ? 'border-red-300 bg-red-50 ring-1 ring-red-200'
          : isFocused ? 'border-green-400 ring-2 ring-green-300' : isRefundItem ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
      }`}
      onClick={isDelivery ? undefined : () => onProductClick?.(product, item.id)}
    >
      {/* Top section: Icon, Name, Brand, Remove */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${
            isRefundItem ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-600'
          }`}>
            {p.image && !imgError ? (
              <img
                src={withCdnImageWidth(p.image, 100)}
                alt={p.name}
                className="w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <ProductIcon name={p.icon} size={20} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-semibold text-sm leading-tight ${isRefundItem ? 'text-red-900' : 'text-gray-900'}`}>
              {isRefundItem && ''}{p.name}
            </p>
            {p.brand && <p className={`text-xs ${isRefundItem ? 'text-red-600' : 'text-gray-500'}`}>{p.brand}</p>}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove(item.id)
          }}
          disabled={editLocked}
          title={lockTooltip}
          className={`p-1.5 rounded-lg ${
            editLocked
              ? 'opacity-50 cursor-not-allowed text-red-300'
              : `cursor-pointer ${
                  isRefundItem ? 'hover:bg-red-200 text-red-600' : 'hover:bg-gray-100 text-gray-400'
                }`
          }`}
        >
          <X size={18} />
        </button>
      </div>

      {/* Badges row — refund state is already conveyed by the row's
          red border/background and the negative price, no need for a
          separate "Refund" label. */}
      <div className="flex items-center gap-1.5 text-xs mb-3 flex-wrap">
        {isMeaningfulStrain(p.strain) && (
          <span className={`px-1.5 py-0.5 rounded font-medium ${strainStyles[p.strain] || 'bg-gray-100 text-gray-600'}`}>
            {p.strain}
          </span>
        )}
        {thc !== null && (
          <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded font-medium">
            THC {formatPercent(thc)}{potencySuffix(unit)}
          </span>
        )}
        {shouldShowCbd(cbd) && (
          <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded font-medium">
            CBD {formatPercent(cbd)}{potencySuffix(unit)}
          </span>
        )}
        {p.size && (
          <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded font-medium">
            {p.size}
          </span>
        )}
        {onHand !== null && (
          <span
            className={`text-xs px-1.5 py-0.5 rounded font-medium ${
              onHand <= 1 ? 'bg-red-50 text-red-600 font-semibold' : 'bg-gray-100 text-gray-600'
            }`}
          >
            IOH: {onHand}
          </span>
        )}
        {/* Scan-to-confirm-lot: a line loaded from a hold/ecom order that
            still needs a confirming scan shows a red prompt; once scanned it
            shows the (physically-confirmed) lot with a green check. Lines
            with no scan_confirmed flag fall back to the plain LOT badge. */}
        {item.scan_confirmed === false ? (
          <span className="bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1">
            <ScanLine className="w-3 h-3" />
            Scan to confirm
          </span>
        ) : item.lot_number ? (
          <span
            className={`text-xs px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 ${
              item.scan_confirmed === true
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-100 text-orange-700'
            }`}
          >
            {item.scan_confirmed === true && <Check className="w-3 h-3" />}
            LOT: {item.lot_number}
          </span>
        ) : null}
        {item.gift_card_number && (
          <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded font-medium">
            #{item.gift_card_number.slice(-4)}
          </span>
        )}
        {product.tags?.map((tag) => (
          <span
            key={tag}
            className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded font-medium"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Rejected-line strip. Carries the BE's own sentence verbatim where it
          gave us one ("Cannot add 1 units. Only 0 units available...") so the
          cashier reads the same reason the commit would have thrown at them. */}
      {blockedReason && (
        <div className="flex items-start gap-2 mb-3 rounded-lg bg-red-100 px-2.5 py-2">
          <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-red-800 leading-tight">Not added to the sale</p>
            <p className="text-xs text-red-700 leading-tight mt-0.5">{blockedReason}</p>
          </div>
        </div>
      )}

      {/* Bottom: Quantity controls + Price */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onQuantityChange(item.id, -1)
            }}
            disabled={isBasket || isGiftCard || editLocked}
            className={`p-2 md:p-1.5 border rounded-lg ${
              isBasket || isGiftCard || editLocked
                ? 'border-gray-100 text-gray-300 cursor-not-allowed'
                : isRefundItem
                  ? 'bg-red-50 border-red-200 hover:bg-red-100 cursor-pointer'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer'
            }`}
            title={
              paymentLocked
                ? lockTooltip
                : isBasket
                  ? 'Baskets are sold as a unit — remove and re-add to change qty'
                  : isGiftCard
                    ? 'Gift cards are 1 per row — use the X to remove'
                    : refundLocked
                      ? 'Updating refund — wait for the server to respond'
                      : undefined
            }
          >
            <Minus size={16} />
          </button>
          <span className={`w-10 text-center font-semibold text-base ${isRefundItem ? 'text-red-900' : 'text-gray-900'}`}>
            {displayQuantity}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onQuantityChange(item.id, 1)
            }}
            disabled={!finalCanIncrease || isBasket || isGiftCard || editLocked}
            className={`p-2 md:p-1.5 border rounded-lg ${
              !finalCanIncrease || isBasket || isGiftCard || editLocked
                ? 'border-gray-100 text-gray-300 cursor-not-allowed'
                : isRefundItem
                  ? 'bg-red-50 border-red-200 hover:bg-red-100 cursor-pointer'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer'
            }`}
            title={
              paymentLocked
                ? lockTooltip
                : isBasket
                  ? 'Baskets are sold as a unit — remove and re-add to change qty'
                  : isGiftCard
                    ? 'Gift cards are 1 per row — each card has its own number'
                    : refundLocked
                      ? 'Updating refund — wait for the server to respond'
                      : saleAtCap
                        ? lotCapHit
                          ? `No more on hand for lot ${item.lot_number}`
                          : 'No more on hand'
                        : !canIncrease
                          ? isRefundItem
                            ? 'Cannot refund more than the original quantity'
                            : undefined
                          : undefined
            }
          >
            <Plus size={16} />
          </button>
        </div>
        <div className="text-right">
          <div className="flex items-baseline justify-end gap-1.5">
            {displayQuantity > 1 && (
              <span className={`text-xs ${isRefundItem ? 'text-red-600' : 'text-gray-500'}`}>
                {formatCurrency(unitPrice)} ea
              </span>
            )}
            <p className={`font-bold text-lg ${
              blockedReason
                ? 'text-gray-400 line-through'
                : isRefundItem ? 'text-red-900' : 'text-gray-900'
            }`}>
              {isRefundItem ? '-' : ''}{formatCurrency(Math.abs(lineTotal))}
            </p>
          </div>
          {hasSavings ? (
            <p className="text-xs leading-tight flex items-baseline justify-end gap-1.5">
              <span className="text-gray-400 line-through">{formatCurrency(regularLineTotal)}</span>
              <span className="text-green-600">(-{formatCurrency(totalSavings)})</span>
            </p>
          ) : Math.abs(rawLineDiscount) > 0.005 ? (
            <p className="text-xs text-green-600">
              -{formatCurrency(Math.abs(rawLineDiscount))} off
              {absQty > 1 ? <> <span className="text-green-700/70">({formatCurrency(perUnitDiscount)}/unit)</span></> : null}
            </p>
          ) : null}
          {blockedReason && (
            <p className="text-xs font-semibold text-red-600 leading-tight">Not charged</p>
          )}
        </div>
      </div>
    </div>
  )
}
